// src/App.js

import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { SubscriptionProvider } from './context/SubscriptionContext';
import { ThemeProvider } from './context/ThemeContext';
import ErrorBoundary from './components/ErrorBoundary';
import { Toaster } from 'react-hot-toast';

import MainLayout from './components/layout/MainLayout';
import Login from './pages/Auth/Login';
import Register from './pages/Auth/Register';
import RegisterPersonnel from './pages/Auth/RegisterPersonnel';
import Dashboard from './pages/Dashboard/Dashboard';
import Settings from './pages/Settings/Settings';
import UserActivity from './pages/UserActivity/UserActivity';
import Subscription from './pages/Subscription/Subscription';
import TodoList from './pages/TodoList/TodoList';
import PersonnelMenu from './pages/Settings/PersonnelMenu';

// Invoices imports
import Invoices from './pages/Invoices/Invoices';
import NewInvoice from './pages/Invoices/NewInvoice';
import SalesInvoices from './pages/SalesInvoices/SalesInvoices';

// Additional page imports
import Reports from './pages/Reports/Reports';
import AgingReport from './pages/Reports/AgingReport';
import CashAndBank from './pages/CashAndBank/CashAndBank';

import Customers from './pages/Customers/Customers';
import CustomerDetail from './pages/Customers/CustomerDetail';
import CustomerPricing from './pages/Customers/CustomerPricing';
import CustomerGroupPricing from './pages/Customers/CustomerGroupPricing';
import Suppliers from './pages/Suppliers/Suppliers';
import SupplierDetail from './pages/Suppliers/SupplierDetail';
import Stock from './pages/Stock/Stock';
import StockDetail from './pages/Stock/StockDetail';
import StockEntry from './pages/StockEntry/StockEntry';
import PurchaseInvoices from './pages/PurchaseInvoices/PurchaseInvoices';
import NewPurchaseInvoice from './pages/PurchaseInvoices/NewPurchaseInvoice';
import Orders from './pages/Orders/Orders';
import OrderDetail from './pages/Orders/OrderDetail';
import NewOrder from './pages/Orders/NewOrder';
import BulkOrderImport from './pages/Orders/BulkOrderImport';
import UninvoicedShipments from './pages/Orders/UninvoicedShipments';
import AllShipments from './pages/Orders/AllShipments';
import NewDirectShipment from './pages/Orders/NewDirectShipment';
import RequestedOrders from './pages/RequestedOrders/RequestedOrders';
import MarketplaceIntegration from './pages/MarketplaceIntegration/MarketplaceIntegration';
import MarketplacePricing from './pages/MarketplacePricing/MarketplacePricing';
import BulkPricing from './pages/MarketplacePricing/BulkPricing';

// Raw Material Purchases imports
import RawMaterialPurchases from './pages/RawMaterialPurchases/RawMaterialPurchases';
import NewRawMaterialPurchase from './pages/RawMaterialPurchases/NewRawMaterialPurchase';

// Production module imports
import ProductionDashboard from './pages/Production/ProductionDashboard';
import Recipes from './pages/Production/Recipes';
import RecipeDetail from './pages/Production/RecipeDetail';
import ProductionOrders from './pages/Production/ProductionOrders';
import ProductionOrderDetailNew from './pages/Production/ProductionOrderDetailNew';
import Subcontractors from './pages/Production/Subcontractors';
import SubcontractorDetail from './pages/Production/SubcontractorDetail';
import CostCalculation from './pages/Production/CostCalculation';
import CostCalculationDetail from './pages/Production/CostCalculationDetail';
import ProfitabilityAnalysis from './pages/Production/ProfitabilityAnalysis';
import ProfitabilityDetail from './pages/Production/ProfitabilityDetail';
import ProductionReports from './pages/Production/Reports';
import ProductionStatusReport from './pages/Production/ProductionStatusReport';
import RawMaterialStockReport from './pages/Production/RawMaterialStockReport';
import SubcontractorProductionReport from './pages/Production/SubcontractorProductionReport';
import SubcontractorBalanceReport from './pages/Production/SubcontractorBalanceReport';
import ProductCostReport from './pages/Production/ProductCostReport';
import WasteReport from './pages/Production/WasteReport';

// Logistics imports
import DriverLogistics from './pages/Logistics/DriverLogistics';

// Warehouse import
import Warehouses from './pages/Warehouses/Warehouses';

// Image Preparation import
import ImagePreparation from './pages/ImagePreparation/ImagePreparation';
import VideoPreparation from './pages/VideoPreparation/VideoPreparation';

// Price Quotes imports
import PriceQuotes from './pages/PriceQuotes/PriceQuotes';
import NewPriceQuote from './pages/PriceQuotes/NewPriceQuote';
import PriceQuoteDetail from './pages/PriceQuotes/PriceQuoteDetail';
import CustomerPortal from './pages/CustomerPortal/CustomerPortal';

// Store Panel imports
import StoreDashboard from './pages/StorePanel/StoreDashboard';
import StoreInventory from './pages/StorePanel/StoreInventory';
import StoreRetailSale from './pages/StorePanel/StoreRetailSale';
import StoreSales from './pages/StorePanel/StoreSales';

function ProtectedRoute({ children, permission }) {
  const { currentUser, userData, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!currentUser) {
    return <Navigate to="/login" />;
  }

  // If permission is required and user doesn't have it, redirect to dashboard
  if (permission && userData) {
    // Admin users have all permissions
    if (userData.role === 'admin') {
      return children;
    }

    // Check if user has the required permission
    if (!userData.permissions || !userData.permissions[permission]) {
      return <Navigate to="/dashboard" />;
    }
  }

  return children;
}

function App() {
  const { currentUser, userData } = useAuth();

  return (
    <ThemeProvider>
      <ErrorBoundary>
        <SubscriptionProvider>
          <Toaster 
            position="top-right"
            toastOptions={{
              duration: 3000,
              style: {
                borderRadius: '10px',
                background: '#333',
                color: '#fff',
                fontSize: '14px'
              },
            }} 
          />
          <Routes>
            <Route path="/" element={
              !currentUser ? <Navigate to="/login" /> :
                userData?.role === 'customer' ? <Navigate to="/portal" /> :
                  userData?.role === 'store' ? <Navigate to="/magaza" /> :
                    <Navigate to="/dashboard" />
            } />

            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/register-personnel/:companyId/:invitationId" element={<RegisterPersonnel />} />

            <Route element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/personnel-menu" element={<PersonnelMenu />} />

              {/* Production module routes */}
              <Route path="/uretim" element={<ProtectedRoute><ProductionDashboard /></ProtectedRoute>} />
              <Route path="/uretim/reçeteler" element={<ProtectedRoute><Recipes /></ProtectedRoute>} />
              <Route path="/uretim/reçeteler/:recipeId" element={<ProtectedRoute><RecipeDetail /></ProtectedRoute>} />
              <Route path="/uretim/emirler" element={<ProtectedRoute><ProductionOrders /></ProtectedRoute>} />
              <Route path="/uretim/emirler/:orderId" element={<ProtectedRoute><ProductionOrderDetailNew /></ProtectedRoute>} />
              <Route path="/uretim/fason-tedarikciler" element={<ProtectedRoute><Subcontractors /></ProtectedRoute>} />
              <Route path="/uretim/fason-tedarikciler/:subcontractorId" element={<ProtectedRoute><SubcontractorDetail /></ProtectedRoute>} />
              <Route path="/uretim/maliyet-hesaplama" element={<ProtectedRoute><CostCalculation /></ProtectedRoute>} />
              <Route path="/uretim/maliyet-hesaplama/:productId" element={<ProtectedRoute><CostCalculationDetail /></ProtectedRoute>} />
              <Route path="/uretim/karlilik-analizi" element={<ProtectedRoute><ProfitabilityAnalysis /></ProtectedRoute>} />
              <Route path="/uretim/karlilik-analizi/:productId" element={<ProtectedRoute><ProfitabilityDetail /></ProtectedRoute>} />
              <Route path="/uretim/raporlar" element={<ProtectedRoute><ProductionReports /></ProtectedRoute>} />
              <Route path="/uretim/raporlar/uretim-durum" element={<ProtectedRoute><ProductionStatusReport /></ProtectedRoute>} />
              <Route path="/uretim/raporlar/hammadde-stok" element={<ProtectedRoute><RawMaterialStockReport /></ProtectedRoute>} />
              <Route path="/uretim/raporlar/fason-uretim" element={<ProtectedRoute><SubcontractorProductionReport /></ProtectedRoute>} />
              <Route path="/uretim/raporlar/fason-bakiye" element={<ProtectedRoute><SubcontractorBalanceReport /></ProtectedRoute>} />
              <Route path="/uretim/raporlar/urun-maliyet" element={<ProtectedRoute><ProductCostReport /></ProtectedRoute>} />
              <Route path="/uretim/raporlar/fire-raporu" element={<ProtectedRoute><WasteReport /></ProtectedRoute>} />

              {/* Logistics routes */}
              <Route path="/lojistik" element={<ProtectedRoute><DriverLogistics /></ProtectedRoute>} />

              <Route path="/raporlar" element={<ProtectedRoute permission="raporlar"><Reports /></ProtectedRoute>} />
              <Route path="/raporlar/yaslandirma" element={<ProtectedRoute permission="raporlar"><AgingReport /></ProtectedRoute>} />

              <Route path="/kasa" element={<ProtectedRoute permission="kasa"><CashAndBank /></ProtectedRoute>} />
              <Route path="/musteriler" element={<ProtectedRoute permission="musteriler"><Customers /></ProtectedRoute>} />
              <Route path="/musteriler/:customerId" element={<ProtectedRoute permission="musteriler"><CustomerDetail /></ProtectedRoute>} />
              <Route path="/musteriler/:customerId/fiyatlar" element={<ProtectedRoute permission="musteriler"><CustomerPricing /></ProtectedRoute>} />
              <Route path="/musteriler/grup-fiyatlandirmalari" element={<ProtectedRoute permission="musteriler"><CustomerGroupPricing /></ProtectedRoute>} />
              <Route path="/tedarikciler" element={<ProtectedRoute permission="tedarikciler"><Suppliers /></ProtectedRoute>} />
              <Route path="/tedarikciler/:supplierId" element={<ProtectedRoute permission="tedarikciler"><SupplierDetail /></ProtectedRoute>} />
              <Route path="/stok" element={<ProtectedRoute permission="stok"><Stock /></ProtectedRoute>} />
              <Route path="/stok/:productId" element={<ProtectedRoute permission="stok"><StockDetail /></ProtectedRoute>} />
              <Route path="/stok-girisi" element={<ProtectedRoute permission="stokGirisi"><StockEntry /></ProtectedRoute>} />
              <Route path="/depolar" element={<ProtectedRoute permission="stok"><Warehouses /></ProtectedRoute>} />
              {/* Invoices routes */}
              <Route path="/faturalar" element={<ProtectedRoute permission="faturalar"><Invoices /></ProtectedRoute>} />
              <Route path="/faturalar/satis" element={<ProtectedRoute permission="faturalar"><SalesInvoices /></ProtectedRoute>} />
              <Route path="/faturalar/yeni" element={<ProtectedRoute permission="faturalar"><NewInvoice /></ProtectedRoute>} />
              <Route path="/alis-faturalari" element={<ProtectedRoute permission="alisFaturalari"><PurchaseInvoices /></ProtectedRoute>} />
              <Route path="/alis-faturalari/yeni" element={<ProtectedRoute permission="alisFaturalari"><NewPurchaseInvoice /></ProtectedRoute>} />
              <Route path="/siparisler" element={<ProtectedRoute permission="siparisler"><Orders /></ProtectedRoute>} />
              <Route path="/siparisler/:orderId" element={<ProtectedRoute permission="siparisler"><OrderDetail /></ProtectedRoute>} />
              <Route path="/siparisler/yeni" element={<ProtectedRoute permission="siparisler"><NewOrder /></ProtectedRoute>} />
              <Route path="/siparisler/toplu-yukle" element={<ProtectedRoute permission="siparisler"><BulkOrderImport /></ProtectedRoute>} />
              <Route path="/siparisler/faturalastirilmamis-sevkiyatlar" element={<ProtectedRoute permission="siparisler"><UninvoicedShipments /></ProtectedRoute>} />
              <Route path="/siparisler/tum-sevkiyatlar" element={<ProtectedRoute permission="siparisler"><AllShipments /></ProtectedRoute>} />
              <Route path="/siparisler/direkt-sevk-irsaliyesi" element={<ProtectedRoute permission="siparisler"><NewDirectShipment /></ProtectedRoute>} />
              <Route path="/istek-siparisler" element={<ProtectedRoute permission="istekSiparisler"><RequestedOrders /></ProtectedRoute>} />
              <Route path="/yapilacaklar" element={<TodoList />} />
              <Route path="/kullanici-aktiviteleri" element={<ProtectedRoute permission="ayarlar"><UserActivity /></ProtectedRoute>} />
              <Route path="/abonelik" element={<Subscription />} />
              <Route path="/ayarlar" element={<ProtectedRoute permission="ayarlar"><Settings /></ProtectedRoute>} />
              <Route path="/marketplace" element={<ProtectedRoute permission="ayarlar"><MarketplaceIntegration /></ProtectedRoute>} />
              {/* Marketplace Pricing routes */}
              <Route path="/pazaryeri-fiyatlama" element={<ProtectedRoute><MarketplacePricing /></ProtectedRoute>} />
              <Route path="/pazaryeri-fiyatlama/toplu" element={<ProtectedRoute><BulkPricing /></ProtectedRoute>} />
              {/* Image Preparation route */}
              <Route path="/gorsel-hazirlama" element={<ProtectedRoute permission="stok"><ImagePreparation /></ProtectedRoute>} />
              <Route path="/video-hazirlama" element={<ProtectedRoute permission="stok"><VideoPreparation /></ProtectedRoute>} />
              {/* Raw Material Purchases routes */}
              <Route path="/hammadde-satin-alma" element={<ProtectedRoute permission="tedarikciler"><RawMaterialPurchases /></ProtectedRoute>} />
              <Route path="/hammadde-satin-alma/yeni" element={<ProtectedRoute permission="tedarikciler"><NewRawMaterialPurchase /></ProtectedRoute>} />
              {/* Price Quotes routes */}
              <Route path="/fiyat-teklifleri" element={<ProtectedRoute permission="siparisler"><PriceQuotes /></ProtectedRoute>} />
              <Route path="/fiyat-teklifleri/yeni" element={<ProtectedRoute permission="siparisler"><NewPriceQuote /></ProtectedRoute>} />
              <Route path="/fiyat-teklifleri/:quoteId" element={<ProtectedRoute permission="siparisler"><PriceQuoteDetail /></ProtectedRoute>} />

              {/* Customer Portal route */}
              <Route path="/portal" element={<ProtectedRoute permission="portal"><CustomerPortal /></ProtectedRoute>} />

              {/* Store Panel routes */}
              <Route path="/magaza" element={<ProtectedRoute permission="magazaPaneli"><StoreDashboard /></ProtectedRoute>} />
              <Route path="/magaza/stok" element={<ProtectedRoute permission="magazaPaneli"><StoreInventory /></ProtectedRoute>} />
              <Route path="/magaza/satis" element={<ProtectedRoute permission="perakendeSatis"><StoreRetailSale /></ProtectedRoute>} />
              <Route path="/magaza/satislar" element={<ProtectedRoute permission="perakendeSatis"><StoreSales /></ProtectedRoute>} />
            </Route>

            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </SubscriptionProvider>
      </ErrorBoundary>
    </ThemeProvider>
  );
}

export default App;